import * as React from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';

import styles from './RepairNamesDialog.module.css';

/** Confirmed catalog name per pasted name, or `null` when skipped. Unreviewed names are absent. */
export type RepairChoices = Record<string, string | null>;

interface Props {
  names: string[];
  choices: RepairChoices;
  onRevisit: (index: number) => void;
}

const plural = (n: number, word: string) =>
  `${n} ${word}${n === 1 ? '' : 'es'}`;

/** What every name became, as the final check before Done; each row reopens that name. */
const RepairSummary: React.FC<Props> = ({ names, choices, onRevisit }) => {
  const matched = names.filter((n) => typeof choices[n] === 'string').length;
  const skipped = names.filter((n) => choices[n] === null).length;
  const open = names.length - matched - skipped;
  return (
    <Box className={styles.summary}>
      <Box className={styles.summaryHead}>
        <TaskAltIcon
          color={open ? 'disabled' : 'success'}
          className={styles.summaryIcon}
        />
        <Box>
          <Typography variant="h6">
            {open ? 'Review so far' : 'All names reviewed'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {plural(matched, 'match')} confirmed
            {skipped > 0 && `, ${skipped} skipped`}
            {open > 0 && `, ${open} not reviewed`}. Click a name to change it.
          </Typography>
        </Box>
      </Box>
      <List dense aria-label="Review" className={styles.summaryList}>
        {names.map((name, i) => {
          const choice = choices[name];
          let outcome = 'Not reviewed';
          if (choice) outcome = choice;
          else if (choice === null) outcome = 'Skipped';
          return (
            <ListItemButton
              key={name}
              onClick={() => onRevisit(i)}
              className={styles.summaryRow}
            >
              <ListItemText
                primary={name}
                primaryTypographyProps={{ noWrap: true }}
                className={styles.summaryFrom}
              />
              <ArrowForwardIcon fontSize="small" color="disabled" aria-hidden />
              <ListItemText
                primary={outcome}
                primaryTypographyProps={{
                  noWrap: true,
                  fontWeight: choice ? 600 : undefined,
                  color: choice ? 'text.primary' : 'text.secondary',
                }}
                className={styles.summaryTo}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};

export default RepairSummary;
